import { lighten } from 'polished';

export type ExerciseLevel = 1 | 2 | 3 | 4 | 5;

export type ExerciseColorMap = Record<ExerciseLevel, string>;

type BaseColors = typeof baseColors;

export interface ThemeColors extends BaseColors {
  /** Optional override used to derive `exerciseColors` via `getDarkExerciseColors`. */
  baseExerciseColor?: string;
}

export interface AppTheme {
  namespace: string;
  colors: ThemeColors;
}

declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme { }
}

export const exerciseColors: ExerciseColorMap = {
  1: '#E54D42',
  2: '#57D011',
  3: '#FB670E',
  4: '#E557FF',
  5: '#FFDA2B',
} as const;

const EXERCISE_LIGHTEN_STEPS: Record<ExerciseLevel, number> = {
  1: 0,
  2: 0.15,
  3: 0.3,
  4: 0.45,
  5: 0.6,
} as const;

export function getDarkExerciseColors(
  baseColor = 'rgb(51, 51, 51)',
): ExerciseColorMap {
  return (Object.keys(EXERCISE_LIGHTEN_STEPS) as unknown as ExerciseLevel[]).reduce(
    (acc, level) => ({
      ...acc,
      [level]: lighten(EXERCISE_LIGHTEN_STEPS[level], baseColor),
    }),
    {} as ExerciseColorMap,
  );
}

// ─── Base Theme Colors ────────────────────────────────────────────────────────

const baseColors = {
  // Brand
  primary: '#0074D9',
  primaryBright: '#0074D9',

  // Semantic
  danger: '#FB670E',
  darkDanger: '#F53D3D',
  success: '#39CA74',
  successLight: '#39CA74',
  warning: '#FFAC61',
  link: '#0074D9',

  // UI
  bgBlack: '#282D30',
  avatarBgColor: '#CCCCCC',
  tag: '#9EA0A1',
  spinner: '#74C8F1',

  // Form
  formLabel: '#0074D9',
  inputColor: '#495057',

  // Layout
  baseBanner: '#0074D9',
  modalHeader: '#0074D9',
  sidebarLink: '#0074D9',

  // Components
  switchActive: '#35CE75',
  rbcEvent: '#0074D9',
  propertyColor: '#0074D9',
  bookmark: '#217EBE',
  bookmarkActive: '#FFC700',

  // Status
  completed: '#39CA74',
  notCompleted: '#EC6E61',

  // Actions
  goalButton: '#39CA74',
  workoutCreate: '#FB670E',
  programCreate: '#FB670E',
  commentCreate: '#E54D42',
  invitationAccepted: '#E557FF',
  exerciseResultCreate: '#FFDA2B',

  // Exercise
  exerciseColors,
} as const

const DEFAULT_NAMESPACE = 'gymcloud'

export function configureTheme(
  namespace: string,
  overrides?: Partial<ThemeColors>,
): AppTheme {
  const isDefault = namespace === DEFAULT_NAMESPACE || !overrides

  const colors: ThemeColors = isDefault
    ? baseColors
    : {
      ...baseColors,
      ...overrides,
      primaryBright: overrides.primary ?? baseColors.primary,
      exerciseColors: getDarkExerciseColors(overrides.baseExerciseColor),
    }

  return { namespace, colors }
}
